import { useState, useContext, useEffect, useCallback } from "react";
import AppContext from "../../store/app-context";
import usePermissions from "../../hooks/use-permissions";

import FormControl from "../../components/Form/FormControl";

import './Settings.scss';
import { FaEdit, FaTimes, FaCamera, FaMapMarkerAlt, FaCheckCircle, FaExclamationCircle, FaQuestionCircle, FaInfoCircle } from 'react-icons/fa';

const PermissionIcon = ({ state }: { state: string }) => {
    if (state === 'granted') return <FaCheckCircle className="perm-icon granted" />;
    if (state === 'denied') return <FaExclamationCircle className="perm-icon denied" />;
    if (state === 'prompt') return <FaQuestionCircle className="perm-icon prompt" />;
    return <FaInfoCircle className="perm-icon unknown" />;
};

const Settings = () => {
    const appCtx = useContext(AppContext);
    const { permissions, isIosPwa, getGuidanceText } = usePermissions();

    const [editing, setEditing] = useState(null);
    const [cameraList, setCameraList] = useState([]);

    const settings:any = appCtx.settings;

    const changeEditing = (event:any) => {
        console.log({event});
        const setting = event.target.id;
        setEditing(setting);
    };

    const cancelEditing = (event:any) => {
        setEditing(null);
    };

    const settingsForm = [
        {
            label: 'Camera',
            setting: 'camera',
            type: 'select',
            data: cameraList,
            update: (event:any) => {
                appCtx.updateSetting('camera', event.target.value);
                cancelEditing(null);
            }
        }
    ];

    const getCameraList = useCallback(() => {
        if (!navigator.mediaDevices?.enumerateDevices) {
            console.log("enumerateDevices() not supported.");
        } else {
            // List cameras and microphones.
            navigator.mediaDevices.enumerateDevices().then((devices) => {
                let cameras:any = [];
                devices.forEach((device) => {
                    if(device.kind === 'videoinput'){
                        cameras.push({
                            value: device.deviceId,
                            label: device.label
                        });
                    }
                });

                console.log({cameras});

                if(cameras.length === 1) appCtx.updateSetting('camera', cameras[0].value);

                setCameraList(cameras);
            }).catch((error) => {
                console.error({error});
            });
        }
    }, [appCtx]);

    const formControls = settingsForm.map((control:any) => {
        let niceValue = '';
        if(control.data){
            control.data.forEach((option:any) => {
                if(option.value === settings[control.setting]) niceValue = option.label;
            });
        }

        return (
            <div key={control.setting} className="setting">
                <strong>{control.label}</strong>
                {control.type === 'select' && <div>
                    {editing !== control.setting && <span id={control.setting} className="link" onClick={changeEditing}>
                        <FaEdit className="" />
                        <span>{niceValue}</span>
                    </span>}
                    {editing === control.setting && <div className="form-control">
                        <FormControl config={{
                            type: control.type,
                            value: settings[control.setting],
                            change: control.update,
                            data: control.data
                        }} />
                        <span>
                            <FaTimes className="link danger" onClick={cancelEditing} />
                        </span>
                    </div>}
                </div>}
            </div>
        );
    });

    useEffect(() => {
        getCameraList();
    }, [getCameraList]);

    return(
        <form id="settings">
            {formControls}

            <div className="permissions-section">
                <h3>Permissions</h3>
                {isIosPwa && (
                    <p className="ios-pwa-note">
                        <FaInfoCircle /> On iOS, permissions may reset when the app restarts. You'll be prompted again when needed.
                    </p>
                )}
                <div className="permission-row">
                    <FaCamera className="perm-type-icon" />
                    <div className="perm-info">
                        <span className="perm-label">Camera</span>
                        <span className="perm-guidance">{getGuidanceText('camera', permissions.camera)}</span>
                    </div>
                    <PermissionIcon state={permissions.camera} />
                </div>
                <div className="permission-row">
                    <FaMapMarkerAlt className="perm-type-icon" />
                    <div className="perm-info">
                        <span className="perm-label">Location</span>
                        <span className="perm-guidance">{getGuidanceText('geolocation', permissions.geolocation)}</span>
                    </div>
                    <PermissionIcon state={permissions.geolocation} />
                </div>
            </div>
        </form>
    );
};

export default Settings;